import React from 'react';

type RefundItem = {
  subtitle: string;
  bullets: string[];
};

type RefundSection = {
  title: string;
  items?: RefundItem[];
  bullets?: string[];
  paragraphs?: string[];
};

const sections: RefundSection[] = [
  {
    title: '1) How FetchTails Works (Payments + Holding Money)',
    bullets: [
      'FetchTails is a marketplace connecting customers and service providers.',
      'When you book, we collect the payment on behalf of the service provider using our payment partner (Cashfree).',
      'Service provider payout is not released immediately. We create a provider "wallet hold/credit" for the appointment and we only release it when the service is actually started and completed (OTP/service verification + completion flow).',
      'Platform commission (example: 10%) is charged from the service provider after successful completion and payout.',
      'FetchTails is not responsible for the quality of service provided by service providers.',
    ],
  },
  {
    title: '2) When You Are Eligible for a Refund',
    items: [
      {
        subtitle: 'A) Customer cancels a paid booking',
        bullets: [
          'Full refund is available only if you cancel at least 24 hours before the scheduled service start time.',
          'If you try to cancel within 24 hours of service start, the full refund cancel option is blocked (no refund).',
          'If the appointment has already started / passed, cancellation is not allowed for refund.',
          'If you want a change instead of cancellation, rescheduling is available only while there are at least 8 hours left before service start.',
          'If the appointment is not paid, cancellation is allowed, but there is no paid money to refund.',
        ],
      },
      {
        subtitle: 'B) Provider declines the booking',
        bullets: [
          'If the service provider declines a booking, you receive a full refund.',
          'This does not depend on the 24-hour cancellation window.',
        ],
      },
      {
        subtitle: 'C) Auto refund for no-show (Service not provided for that day)',
        bullets: [
          'If the provider did not start/provide service by the end of the scheduled appointment day, the system can auto-cancel and refund.',
          "The system checks the appointment's scheduled date and looks for bookings that are still not started.",
          'If OTP/service-start was already verified (service started), auto no-show refund will not run.',
          'The auto job only targets appointments that are still in the "not completed" states (pending/confirmed/in-progress), based on how our app stores appointment status.',
          'If the booking is paid: full refund is triggered.',
          'If the booking is not paid: the appointment is auto-cancelled (no refund money).',
        ],
      },
      {
        subtitle: 'D) When refunds are not provided',
        bullets: [
          'If the appointment is completed.',
          'If the appointment has started (for example OTP verified / service started in normal cases).',
          'If you cancel after the eligible timing window for a paid booking (less than 24 hours before start).',
          'If the system detects that a refund was already issued for the same booking (to prevent duplicate refunds).',
        ],
      },
    ],
  },
  {
    title: '3) What Happens When a Refund Is Triggered',
    bullets: [
      'First, we stop provider payout for the cancelled/no-show appointment. If a provider wallet hold/credit exists for that appointment, we remove that hold so the provider does not get paid.',
      'Then we try to refund you back to the original payment method (bank/UPI/card) using the Cashfree order reference stored with your payment.',
      'The refund amount is based on the total amount you paid for that appointment.',
      'If the original payment refund cannot be initiated, we credit the refund amount into your FetchTails wallet as a fallback.',
      'Finally, we save a refund record for that appointment so our team can track it per booking. Refund records are stored in refund_transactions (wallet fallback is also tracked).',
    ],
  },
  {
    title: '4) Refund Records (What We Save for Every Booking)',
    bullets: [
      'refund_transactions stores: appointment_id, user_id, refund amount, refund type (original payment or wallet), refund method, and the related payment reference when available.',
      'If the refund uses wallet fallback, we also save the customer wallet credit in wallet_transactions.',
    ],
  },
  {
    title: '5) Timing: How Long Refunds Take',
    bullets: [
      'Wallet fallback: you can see the credited amount immediately after the system completes the wallet credit step.',
      'Original payment refund (bank/UPI/card): the refund is initiated in the system quickly, but bank/payment reflection can take a few business days depending on your bank and payment rail.',
    ],
  },
  {
    title: '6) Duplicate Refund Safety (Worst Case)',
    bullets: [
      'Our auto refund job checks if refund_transactions already exists for the same appointment. If it already exists, the system skips to avoid duplicate refunds.',
      'If Cashfree refund fails (example: missing order reference), we automatically use wallet fallback so you still get the value.',
    ],
  },
  {
    title: '7) Reschedule vs Refund (Important)',
    bullets: [
      'You can reschedule only until 8 hours before the service start time.',
      'If you reschedule successfully, the appointment date/time changes. The refund rules depend on the new scheduled time and appointment status.',
    ],
  },
  {
    title: '8) Support',
    paragraphs: [
      'If you need help with a refund, please share your appointment/booking id and the service date. Our team will review the appointment status and the saved refund record (refund_transactions) to confirm what happened.',
      'If you requested a cancellation refund, the wallet fallback (if used) should reflect quickly, while original-source refunds can take a few business days depending on your bank/payment method.',
    ],
  },
];

const CancellationRefunds: React.FC = () => (
  <div className="pt-20 pb-16 bg-gradient-to-br from-primary-50 to-white min-h-screen">
    <section className="container-custom max-w-4xl">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-secondary-800 mb-4">Refund Policy</h1>
        <p className="text-sm text-secondary-500 mb-4">Last updated: April 18, 2026</p>
        <p className="text-secondary-700 leading-relaxed">
          If you need to cancel or a service is not provided, this policy explains exactly when refunds are available
          and how we process them inside FetchTails. The rules here are based on how the app works: appointment
          status, OTP/service start verification, and our refund system.
        </p>
      </div>

      <div className="space-y-6">
        {sections.map((section) => (
          <article key={section.title} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
            <h2 className="text-xl md:text-2xl font-bold text-secondary-800 mb-4">{section.title}</h2>

            {section.items?.map((item) => (
              <div key={item.subtitle} className="mb-5 last:mb-0">
                <h3 className="text-lg font-semibold text-secondary-800 mb-3">{item.subtitle}</h3>
                <ul className="space-y-2">
                  {item.bullets.map((bullet) => (
                    <li key={bullet} className="text-secondary-700 leading-relaxed">
                      • {bullet}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {section.bullets && (
              <ul className="space-y-2">
                {section.bullets.map((bullet) => (
                  <li key={bullet} className="text-secondary-700 leading-relaxed">
                    • {bullet}
                  </li>
                ))}
              </ul>
            )}

            {section.paragraphs?.map((paragraph) => (
              <p key={paragraph} className="text-secondary-700 leading-relaxed mb-3 last:mb-0">
                {paragraph}
              </p>
            ))}
          </article>
        ))}
      </div>
    </section>
  </div>
);

export default CancellationRefunds;
